// src/components/NotificationCapsule.js
//
// project-log/initiatives/navbar-capsula-notificaciones.md — Fases 1-3 y 5 implementadas.
// Layout.js arma el array `signals` a partir de los tres hooks reales (useUnseenFeedbackCount,
// usePromotionAck, useMentorAssignedAck) y lo pasa como prop; este componente no conoce Firestore.
// MOCK_SIGNALS abajo es solo el default para uso aislado del componente (ej. Storybook futuro).
//
// Fusiona perfil + acceso a "Hoy" + campana de notificaciones en una sola cápsula
// (rounded-full, separador fino) en vez de íconos flotantes sueltos — vocabulario Savia:
// panel blanco, rounded-2xl, chips de color por fila, sin borde dorado skeuomorphic.
// Pendiente: Fase 6 (QA visual en dispositivo real / dark mode / isla dinámica).

import React from 'react';
import { Link } from 'gatsby';
import { useLanguage } from './LanguageProvider';

const UserCircleIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const BellIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
  </svg>
);

// Datos de ejemplo — Fase 2 los reemplaza por los tres hooks reales pasados como prop `signals`.
const MOCK_SIGNALS = [
  {
    id: 'feedback',
    chip: 'sage',
    chipLabel: 'Feedback',
    label: 'Tu mentor dejó un comentario en una respuesta',
  },
  {
    id: 'promotion',
    chip: 'gold',
    chipLabel: 'Ascenso',
    label: 'Tu rol cambió — ahora eres mentor',
  },
  {
    id: 'mentor',
    chip: 'forest',
    chipLabel: 'Acompañante',
    label: 'Ya tienes un acompañante asignado',
  },
];

const CHIP_CLASSES = {
  sage: 'bg-brand-sage/15 text-brand-sage dark:bg-brand-sage/20 dark:text-brand-sage',
  gold: 'bg-brand-gold/15 text-brand-gold-dark dark:bg-brand-gold/20 dark:text-brand-gold',
  forest: 'bg-brand-forest/10 text-brand-forest dark:bg-brand-parchment/10 dark:text-brand-parchment',
};

export default function NotificationCapsule({
  variant = 'desktop', // 'desktop' | 'mobile'
  isHome = false,
  accountHref = '/mi-cuenta/',
  accountLabel: accountLabelProp,
  accountAriaLabel,
  signals = MOCK_SIGNALS,
  signalsOnly = false, // omite el segmento de cuenta — para cuando ya hay acceso a "Hoy" en navItems
  floating = true, // false cuando el padre (ej. barra superior móvil) ya se encarga de fijar posición
}) {
  const { t } = useLanguage();
  // Con sesión, "Hoy" ya vive en navItems — este segmento apunta a otro destino
  // (típicamente /ajustes/), así que el label lo decide quien usa el componente
  // en vez de asumir siempre "Hoy" (navegacion-hoy-entrada-salida.md).
  const accountLabel = accountLabelProp || t('notifications.today');
  const notificationsAriaLabel = t('notifications.bellAriaLabel');
  const emptyLabel = t('notifications.empty');
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const hasSignals = signals.length > 0;
  const isMobile = variant === 'mobile';

  const capsuleColorClasses = isHome && !isMobile
    ? 'bg-white/15 backdrop-blur text-brand-parchment'
    : 'bg-white dark:bg-brand-dark-card text-brand-forest dark:text-brand-parchment';

  const dividerColorClasses = isHome && !isMobile
    ? 'border-white/20'
    : 'border-brand-forest/10 dark:border-brand-parchment/10';

  const hoverColorClasses = isHome && !isMobile
    ? 'hover:text-brand-gold'
    : 'hover:text-brand-gold-dark dark:hover:text-brand-gold';

  return (
    <div
      ref={rootRef}
      className={`relative flex items-center rounded-full shadow-sm flex-shrink-0 ${capsuleColorClasses}`}
      style={isMobile && floating ? { top: 'calc(env(safe-area-inset-top) + 0.5rem)', left: '0.5rem', position: 'fixed', zIndex: 50 } : undefined}
    >
      {!signalsOnly && (
        <>
          <Link
            to={accountHref}
            aria-label={accountAriaLabel}
            className={`flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-l-full transition-colors ${hoverColorClasses}`}
          >
            <UserCircleIcon className="w-6 h-6 flex-shrink-0" />
            {!isMobile && (
              <span className="text-sm font-semibold whitespace-nowrap">{accountLabel}</span>
            )}
          </Link>

          <span className={`h-5 border-l ${dividerColorClasses}`} />
        </>
      )}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={notificationsAriaLabel}
        aria-expanded={open}
        className={`relative flex items-center justify-center pl-2.5 pr-2.5 py-1.5 transition-colors ${signalsOnly ? 'rounded-full' : 'rounded-r-full'} ${hoverColorClasses}`}
      >
        <BellIcon className="w-5 h-5" />
        {hasSignals && (
          <span className="absolute top-1 right-1.5 w-2.5 h-2.5 rounded-full bg-brand-gold border-2 border-white dark:border-brand-dark-card" />
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 w-72 bg-white dark:bg-brand-dark-card rounded-2xl shadow-lg border border-brand-gold/10 dark:border-brand-forest py-2 z-50 ${
            isMobile ? 'left-0' : 'right-0'
          }`}
        >
          {hasSignals ? (
            signals.map((signal) => {
              const rowClasses = 'flex flex-col gap-1 px-4 py-2.5 w-full text-left hover:bg-brand-parchment dark:hover:bg-brand-forest/30 transition-colors';
              const rowContent = (
                <>
                  <span className={`self-start px-2 py-0.5 rounded-full text-xs font-semibold ${CHIP_CLASSES[signal.chip] || CHIP_CLASSES.forest}`}>
                    {signal.chipLabel}
                  </span>
                  <p className="text-sm font-medium text-brand-forest dark:text-brand-parchment leading-snug">
                    {signal.label}
                  </p>
                </>
              );
              const handleRowClick = () => {
                if (signal.onDismiss) signal.onDismiss();
                setOpen(false);
              };
              if (signal.href) {
                return (
                  <Link key={signal.id} to={signal.href} className={rowClasses} onClick={handleRowClick}>
                    {rowContent}
                  </Link>
                );
              }
              return (
                <button key={signal.id} type="button" className={rowClasses} onClick={handleRowClick}>
                  {rowContent}
                </button>
              );
            })
          ) : (
            <p className="px-4 py-3 text-sm text-brand-sage dark:text-brand-parchment/60">{emptyLabel}</p>
          )}
          {!signalsOnly && (
            <Link
              to="/mi-cuenta/mensajes/"
              onClick={() => setOpen(false)}
              className="block mt-1 pt-2 px-4 pb-2.5 border-t border-brand-forest/10 dark:border-brand-parchment/10 text-sm font-semibold text-brand-forest dark:text-brand-parchment hover:text-brand-gold-dark dark:hover:text-brand-gold transition-colors"
            >
              {t('notifications.viewAllMessages')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
